import { isEmpty, isNil } from 'lodash'
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'

import { getUserService, updateUserPaymentDestinationsService, updateUserService } from '../services/userServices'
import { RequestResponse } from '../../../shared/services/requestServices'
import {
    PostUserContract,
    UseResendContract,
    UpdateUser,
    User,
    UpdateUserPaymentDestination
} from '../../../shared/models/userModel'
import { useMe } from '../../../shared/hooks/useMe'
import { createUserContractService, resendUserContractService } from '@/shared/services/contractServices'
import { In } from '@hello-aria/query-string'
import { listOnboardingsServices } from '@/shared/services/onboardingServices'

export type AdvanceAttachmentDetails = {
    advanceId: string
    attachmentId: string
    isDownload: boolean
}

export const userQueryKeys = {
    users: ['users'],
    user: (id: string | undefined): (string | undefined)[] => [...userQueryKeys.users, id],
    attachment: (attachmentId: string, isDownload: boolean): string[] => [
        'attachment',
        attachmentId,
        isDownload.toString()
    ]
}

export const useUserById = (
    userId: string | undefined,
    hasBankAccount = true
): UseQueryResult<User | undefined, Error> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: [...userQueryKeys.user(userId), companyId],
        queryFn: async () => {
            const user = await getUserService({ userId, companyId, hasBankAccount })
            if (isNil(user?.body)) return undefined
            const onboardingFilter = In('targetId', [userId as string])

            const onboardings = await listOnboardingsServices({ companyId, query: { filters: [onboardingFilter] } })
            return {
                ...user,
                body: {
                    ...user?.body,
                    onboarding: onboardings?.body?.[0]
                }
            }
        },
        enabled: !isNil(userId) && !isEmpty(userId),
        select: (data) => {
            return data?.body
        }
    })
}

export const useUpdateUser = (): UseMutationResult<RequestResponse<User> | undefined, unknown, UpdateUser, unknown> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => updateUserService(payload, companyId)
    })
}

export const useDeleteUserPaymentDestination = (): UseMutationResult<
    RequestResponse<User> | undefined,
    unknown,
    UpdateUserPaymentDestination,
    unknown
> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: UpdateUserPaymentDestination) =>
            updateUserPaymentDestinationsService(payload, companyId, 'delete')
    })
}

export const useCreateUserPaymentDestination = (): UseMutationResult<
    RequestResponse<User> | undefined,
    unknown,
    UpdateUserPaymentDestination,
    unknown
> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => updateUserPaymentDestinationsService(payload, companyId, 'create')
    })
}

export const usePostUserContract = (): UseMutationResult<
    RequestResponse<PostUserContract> | undefined,
    unknown,
    PostUserContract,
    unknown
> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (data: PostUserContract) =>
            createUserContractService({
                companyId,
                userId: data.id
            })
    })
}

export const useResendUserContract = (): UseMutationResult<
    RequestResponse<void> | undefined,
    unknown,
    UseResendContract,
    unknown
> => {
    const { companyId } = useMe()
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: UseResendContract) =>
            resendUserContractService({
                companyId,
                userId: data.id
            }),
        onSuccess: (_, data) =>
            queryClient.invalidateQueries({
                queryKey: userQueryKeys.user(data.id)
            })
    })
}

export const useInvalidateUser = () => {
    const queryClient = useQueryClient()

    const invalidateUser = async (userId: string) => {
        await queryClient.invalidateQueries({
            queryKey: userQueryKeys.user(userId)
        })
    }

    return {
        invalidateUser
    }
}
